import { useStore } from '@nanostores/react';
import { useEffect, useRef, useState } from 'react';
import {
  setEventStatus,
  setConnectionState,
  clearAllEventStatuses,
  type EventState,
  type EventErrorState,
  type EventErrorDetails,
  connectionStateStore,
} from '~/lib/stores/eventStatus';
import { ensureEventStatusConnection, isConnected, closeConnection } from '~/lib/utils/eventStatusConnection';
import { useEventStatusSync } from '~/hooks/useEventStatusSync';
import * as Sentry from '@sentry/remix';

interface EventStatusProviderProps {
  /**
   * Optional project ID to filter events
   */
  projectId?: string;

  /**
   * Optional chat ID to filter events
   */
  chatId?: string;

  /**
   * Optional user ID to filter events
   */
  userId?: string;

  /**
   * Whether to enable automatic reconnection on connection loss
   * @default true
   */
  autoReconnect?: boolean;

  /**
   * Maximum number of reconnection attempts
   * @default 5
   */
  maxReconnectAttempts?: number;

  /**
   * Base delay between reconnection attempts in milliseconds
   * Uses exponential backoff: delay * (2 ^ attempt)
   * @default 1000
   */
  reconnectDelay?: number;

  /**
   * Whether to clear all event statuses when component unmounts
   * @default false
   */
  clearOnUnmount?: boolean;

  /**
   * Callback when connection is established
   */
  onConnected?: () => void;

  /**
   * Callback when connection is lost
   */
  onDisconnected?: () => void;

  /**
   * Callback when an error occurs
   */
  onError?: (error: string) => void;

  /**
   * Children components
   */
  children?: React.ReactNode;
}

/**
 * EventStatusProvider component
 *
 * Establishes SSE connection to monitor event status changes from your events table
 * and updates the global eventStatus store.
 *
 * @example
 * ```tsx
 * // Monitor specific events:
 * function App() {
 *   return (
 *     <EventStatusProvider
 *       eventIds={['evt-1', 'evt-2']}
 *       onConnected={() => console.log('Connected!')}
 *     >
 *       <YourAppComponents />
 *     </EventStatusProvider>
 *   );
 * }
 * ```
 *
 * @example
 * ```tsx
 * // Monitor all events for a project:
 * function ProjectView({ projectId }) {
 *   return (
 *     <EventStatusProvider projectId={projectId}>
 *       <YourProjectComponents />
 *     </EventStatusProvider>
 *   );
 * }
 * ```
 */
export function EventStatusProvider({
  projectId,
  chatId,
  userId,
  autoReconnect = true,
  maxReconnectAttempts = 5,
  reconnectDelay = 10000,
  clearOnUnmount = false,
  onConnected,
  onDisconnected,
  onError,
  children,
}: EventStatusProviderProps) {
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const isUnmountingRef = useRef(false);
  const hasConnectedRef = useRef(false);
  console.log('projectId from client >>>', 'projectId', projectId, 'chatId', chatId, 'userId', userId);

  // Sync event status store to IndexedDB
  useEventStatusSync(true);

  const connect = () => {
    if (isUnmountingRef.current) {
      return;
    }

    // Clean up existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }

    // Build URL with filters
    const params = new URLSearchParams();

    if (projectId) {
      params.set('projectId', projectId);
    }

    if (chatId) {
      params.set('chatId', chatId);
    }

    if (userId) {
      params.set('userId', userId);
    }

    const url = `/api/event-status-stream${params.toString() ? `?${params.toString()}` : ''}`;

    console.log('[EventStatusProvider] Connecting to:', url);
    setConnectionState({ isConnecting: true, error: null });

    try {
      const eventSource = new EventSource(url);
      eventSourceRef.current = eventSource;

      console.log('[EventStatusProvider] EventSource created, readyState:', eventSource.readyState);

      // Handle successful connection open
      eventSource.onopen = () => {
        console.log('[EventStatusProvider] Connection opened');
        hasConnectedRef.current = true;
        setConnectionState({
          isConnected: true,
          isConnecting: false,
          error: null,
          reconnectAttempts: 0,
        });
        setReconnectAttempts(0);

        // onConnected?.();
      };

      // Handle connection established event (custom event from server)
      eventSource.addEventListener('connected', (event) => {
        console.log('[EventStatusProvider] Connected event received:', event.data);

        if (!hasConnectedRef.current) {
          hasConnectedRef.current = true;
          setConnectionState({
            isConnected: true,
            isConnecting: false,
            error: null,
            reconnectAttempts: 0,
          });
          setReconnectAttempts(0);
          onConnected?.();
        }
      });

      // Handle server-initiated close (custom "server-closed" event from backend)
      eventSource.addEventListener('server-closed', (event: MessageEvent) => {
        try {
          const data = JSON.parse(event.data || '{}') as {
            reason?: string;
            timestamp?: string;
          };

          console.log('[EventStatusProvider] Server-closed event received:', data);
        } catch {
          console.log('[EventStatusProvider] Server-closed event received (non-JSON payload)');
        }

        // Close the EventSource on client side
        if (eventSourceRef.current) {
          eventSourceRef.current.close();
          eventSourceRef.current = null;
        }

        // Update connection state to reflect a clean server close, without treating as an error
        setConnectionState({
          isConnected: false,
          isConnecting: false,
          error: null,
          serverClosed: true,
          closeReason: 'server-closed',
          reconnectAttempts: 0,
        });

        // Notify consumer that we are disconnected
        onDisconnected?.();
      });

      // Handle status updates
      eventSource.addEventListener('status-update', (event) => {
        try {
          const data = JSON.parse(event.data) as {
            eventId: string;
            status: string;
            eventName?: string;
            projectId?: string;
            chatId?: string;
            userId?: string;
            timestamp: string;
            error?: EventErrorDetails;
          };

          console.log('[EventStatusProvider] Status update:', data);

          const derivedEventState: EventErrorState | undefined =
            data.error?.errorType ?? (data.status === 'FAILED' ? 'PROCESSING_ERROR' : undefined);

          const eventState: EventState = {
            status: data.status as any,
            eventState: derivedEventState,
            projectId: data.projectId || '',
            chatId: data.chatId || '',
            userId: data.userId || '',
            timestamp: data.timestamp,
            error: data.error,
          };

          setEventStatus(data.eventId, eventState);
        } catch (error) {
          console.error('[EventStatusProvider] Error parsing status update:', error);
          closeConnection();
        }
      });

      // Handle periodic snapshot updates (server pushes latest matching rows)
      eventSource.addEventListener('snapshot', (event) => {
        try {
          const payload = JSON.parse(event.data) as {
            events?: Array<{
              eventId: string;
              status: string;
              eventName?: string;
              projectId?: string;
              chatId?: string;
              userId?: string;
              timestamp: string;
            }>;
          };

          if (payload?.events && Array.isArray(payload.events)) {
            for (const ev of payload.events) {
              const derivedEventState: EventErrorState | undefined =
                ev.status === 'FAILED' ? 'PROCESSING_ERROR' : undefined;

              setEventStatus(ev.eventId, {
                status: ev.status as any,
                eventState: derivedEventState,
                projectId: ev.projectId || '',
                chatId: ev.chatId || '',
                userId: ev.userId || '',
                timestamp: ev.timestamp,
              });
            }
          }
        } catch (e) {
          console.error('[EventStatusProvider] Error parsing snapshot:', e);
          closeConnection();
        }
      });

      // (Heartbeat removed — server now sends periodic snapshots)

      // Handle error messages sent from server as custom 'error' events
      eventSource.addEventListener('error', (event: any) => {
        if (event.data) {
          try {
            const errorData = JSON.parse(event.data);
            const errorMessage = errorData?.error || errorData?.message || 'Server error';
            console.error('[EventStatusProvider] Server error event:', errorMessage);

            // Close the connection when server sends an error
            if (eventSourceRef.current) {
              eventSourceRef.current.close();
              eventSourceRef.current = null;
            }

            setConnectionState({
              isConnected: false,
              isConnecting: false,
              error: errorMessage,
            });

            onError?.(errorMessage);

            // Trigger reconnection logic if enabled
            if (autoReconnect && reconnectAttempts < maxReconnectAttempts && !isUnmountingRef.current) {
              const delay = reconnectDelay * Math.pow(2, reconnectAttempts);
              console.log(
                `[EventStatusProvider] Reconnecting after server error in ${delay}ms (attempt ${reconnectAttempts + 1}/${maxReconnectAttempts})`,
              );

              setConnectionState({
                reconnectAttempts: reconnectAttempts + 1,
              });

              reconnectTimeoutRef.current = setTimeout(() => {
                setReconnectAttempts((prev) => prev + 1);

                if (!userId || !projectId) {
                  return;
                }

                connect();
              }, delay);
            }
          } catch (e) {
            console.error('[EventStatusProvider] Error parsing server error:', event.data);
            closeConnection();
          }
        } else {
          closeConnection();
        }
      });

      // Handle connection errors (browser-level EventSource errors)
      eventSource.onerror = (error) => {
        console.error('[EventStatusProvider] Connection error, readyState:', eventSource.readyState);

        // Only process if not already unmounting
        if (isUnmountingRef.current) {
          return;
        }

        const errorMessage = hasConnectedRef.current ? 'Connection lost' : 'Failed to connect';

        setConnectionState({
          isConnected: false,
          isConnecting: false,
          error: errorMessage,
        });

        if (hasConnectedRef.current) {
          onDisconnected?.();
        } else {
          onError?.(errorMessage);
        }

        // Close the failed connection
        if (eventSourceRef.current) {
          eventSourceRef.current.close();
          eventSourceRef.current = null;
        }

        // Attempt to reconnect if enabled
        if (autoReconnect && reconnectAttempts < maxReconnectAttempts) {
          const delay = reconnectDelay * Math.pow(2, reconnectAttempts);
          console.log(
            `[EventStatusProvider] Reconnecting in ${delay}ms (attempt ${reconnectAttempts + 1}/${maxReconnectAttempts})`,
          );

          setConnectionState({
            reconnectAttempts: reconnectAttempts + 1,
          });

          reconnectTimeoutRef.current = setTimeout(() => {
            setReconnectAttempts((prev) => prev + 1);

            if (!userId || !projectId) {
              return;
            }

            connect();
          }, delay);
        } else if (reconnectAttempts >= maxReconnectAttempts) {
          const maxAttemptsError = 'Maximum reconnection attempts reached';
          console.error('[EventStatusProvider]', maxAttemptsError);
          setConnectionState({ error: maxAttemptsError });
          onError?.(maxAttemptsError);
          closeConnection();
        }
      };
    } catch (error) {
      Sentry.captureException(`Error from EventStatusProvider in EventStatusProvider.tsx: ${error}`, {
        extra: {
          projectId,
          chatId,
          userId,
        },
      });
      console.error('[EventStatusProvider] Failed to create EventSource:', error);
      setConnectionState({
        isConnected: false,
        isConnecting: false,
        error: 'Failed to create connection',
      });
      onError?.('Failed to create connection');
      closeConnection();
    }
  };

  // Initialize connection using global connection manager
  useEffect(() => {
    hasConnectedRef.current = false;

    if (!userId || !projectId) {
      return;
    }

    // Use global connection manager to ensure connection exists
    void ensureEventStatusConnection(projectId, chatId, userId)
      .then(() => {
        console.log('[EventStatusProvider] Connection established via global manager');
        hasConnectedRef.current = true;
        onConnected?.();
      })
      .catch((error) => {
        console.error('[EventStatusProvider] Failed to establish connection:', error);
        onError?.(error instanceof Error ? error.message : String(error));
      });

    // Cleanup on unmount
    // eslint-disable-next-line consistent-return
    return () => {
      if (userId && projectId) {
        isUnmountingRef.current = true;

        console.log('[EventStatusProvider] Cleaning up...');

        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = null;
        }

        /*
         * Note: We don't close the global connection here as it might be used by other components
         * Only clear local refs
         */
        if (eventSourceRef.current) {
          eventSourceRef.current = null;
        }

        if (clearOnUnmount) {
          clearAllEventStatuses();
        }
      }
    };
  }, [projectId, chatId, userId]); // Reconnect if filters change

  return <>{children}</>;
}

/**
 * Helper component to display connection status (useful for debugging)
 */
export function EventStatusConnectionIndicator() {
  const { isConnected, isConnecting, error, reconnectAttempts, serverClosed } = useStore(connectionStateStore);

  const getStatusColor = () => {
    if (error) {
      return 'text-red-500';
    }

    if (isConnected) {
      return 'text-green-500';
    }

    if (isConnecting) {
      return 'text-yellow-500';
    }

    return 'text-gray-500';
  };

  const getStatusText = () => {
    if (serverClosed) {
      return 'Disconnected (server closed)';
    }

    if (error) {
      return `Error: ${error}`;
    }

    if (isConnected) {
      return 'Connected';
    }

    if (isConnecting) {
      return 'Connecting...';
    }

    return 'Disconnected';
  };

  return (
    <div className={`text-xs ${getStatusColor()}`}>
      <span>● {getStatusText()}</span>
      {reconnectAttempts > 0 && <span> (Attempt {reconnectAttempts})</span>}
    </div>
  );
}

